const PAYLOAD_COLUMN_DESTINATIONS = new Set<_PayloadColumn.Destination>(['customer', 'contract']);

export namespace _PayloadColumn {
  export type Destination = 'customer' | 'contract';

  export type Parsed = {
    destination: Destination;
    columnName: string;
  };

  /**
   * `"<destination>.<columnName>"` 形式の文字列をパースする。
   *
   * - 区切りは最初の `.` のみ（列名に `.` を含めてよい）
   * - destination は `customer` または `contract` のみ許可
   * - 不正な形式は `null` を返す
   */
  export const Parse = (value: string): Parsed | null => {
    const dotIndex = value.indexOf('.');
    if (dotIndex <= 0 || dotIndex === value.length - 1) {
      return null;
    }

    const destination = value.slice(0, dotIndex);
    if (!PAYLOAD_COLUMN_DESTINATIONS.has(destination as Destination)) {
      return null;
    }

    return {
      destination: destination as Destination,
      columnName: value.slice(dotIndex + 1),
    };
  };

  /** destination と列名から `"<destination>.<columnName>"` 形式の文字列を構築する。 */
  export const Format = (destination: Destination, columnName: string): string => `${destination}.${columnName}`;

  /**
   * payload の key を `{ destination, columnName }` に解決する。
   *
   * - `"customer.<列名>"` / `"contract.<列名>"` 形式を優先
   * - プレフィックスなしの列名は、allowedPrefixedColumns 内で一意に特定できる場合のみ許可
   */
  export const ResolvePayloadKey = (key: string, allowedPrefixedColumns: readonly string[]): Parsed | null => {
    const prefixed = Parse(key);
    if (prefixed) {
      const formatted = Format(prefixed.destination, prefixed.columnName);
      return allowedPrefixedColumns.includes(formatted) ? prefixed : null;
    }

    const matches: Parsed[] = [];
    for (const entry of allowedPrefixedColumns) {
      const parsed = Parse(entry);
      if (parsed && parsed.columnName === key) {
        matches.push(parsed);
      }
    }
    return matches.length === 1 ? matches[0] : null;
  };

  const describeUnresolvedKey = (key: string, allowedPrefixedColumns: readonly string[], label: string): string | null => {
    const prefixed = Parse(key);
    if (prefixed) {
      const formatted = Format(prefixed.destination, prefixed.columnName);
      if (!allowedPrefixedColumns.includes(formatted)) {
        return `キー \`${key}\` は${label}に含まれていません。利用可能なキー: ${allowedPrefixedColumns.join(', ')}`;
      }
    }

    const columnNameMatches = allowedPrefixedColumns.filter((entry) => {
      const entryParsed = Parse(entry);
      return entryParsed?.columnName === key;
    });
    if (columnNameMatches.length > 1) {
      return `列名 \`${key}\` は customer / contract の両方に存在するため、\`customer.${key}\` または \`contract.${key}\` 形式で指定してください。`;
    }

    return `キー \`${key}\` を${label}の列として解決できません。利用可能なキー: ${allowedPrefixedColumns.join(', ')}`;
  };

  const collectResolvedKeys = (
    payload: Record<string, unknown>,
    allowedPrefixedColumns: readonly string[],
    label: string,
  ): { matched: Map<string, string> } | { error: string } => {
    const matched = new Map<string, string>();
    for (const key of Object.keys(payload)) {
      const parsed = ResolvePayloadKey(key, allowedPrefixedColumns);
      if (!parsed) {
        return { error: describeUnresolvedKey(key, allowedPrefixedColumns, label) ?? `キー \`${key}\` を解決できません。` };
      }

      const columnKey = Format(parsed.destination, parsed.columnName);
      const existingKey = matched.get(columnKey);
      if (existingKey) {
        return { error: `キー \`${key}\` と \`${existingKey}\` が同一の列 \`${columnKey}\` を指しています。` };
      }
      matched.set(columnKey, key);
    }
    return { matched };
  };

  /**
   * Customer DB verify 用: 契約一意キー列は必須、その他 schema 列は任意。
   * 値そのものは含めず、キー名・列名のみを返す（PII 漏洩防止）。
   */
  export const DescribeVerifyFieldsMismatch = (
    verifyFields: Record<string, unknown>,
    params: {
      requiredPrefixedColumns: readonly string[];
      allowedPrefixedColumns: readonly string[];
    },
  ): string | null => {
    const { requiredPrefixedColumns, allowedPrefixedColumns } = params;

    const collected = collectResolvedKeys(verifyFields, allowedPrefixedColumns, 'スキーマ');
    if ('error' in collected) {
      return collected.error;
    }

    const missingRequired = requiredPrefixedColumns.filter((c) => !collected.matched.has(c));
    if (missingRequired.length > 0) {
      return `契約一意キーの列が verifyFields に不足しています: ${missingRequired.join(', ')}`;
    }

    return null;
  };

  /** Customer DB verify 用の verifyFields 検証。 */
  export const VerifyFieldsKeysMatch = (
    verifyFields: Record<string, unknown>,
    params: {
      requiredPrefixedColumns: readonly string[];
      allowedPrefixedColumns: readonly string[];
    },
  ): boolean => DescribeVerifyFieldsMismatch(verifyFields, params) === null;

  export type ReturnFieldsInput = {
    customer?: string[];
    contract?: string[];
  };

  const hasDuplicate = (names: readonly string[]): string | null => {
    const seen = new Set<string>();
    for (const name of names) {
      if (seen.has(name)) {
        return `返却列 \`${name}\` が重複しています。`;
      }
      seen.add(name);
    }
    return null;
  };

  /**
   * Customer DB verify 用: returnFields の列存在・destination 整合・重複を検証する。
   * 未指定・全 destination が空の場合は `null`（有効）を返す。
   */
  export const DescribeReturnFieldsMismatch = (
    returnFields: ReturnFieldsInput | undefined,
    schemaColumns: readonly { name: string; destination: string }[],
  ): string | null => {
    if (returnFields === undefined) {
      return null;
    }

    for (const key of Object.keys(returnFields)) {
      if (key !== 'customer' && key !== 'contract') {
        return `returnFields に未知のキー \`${key}\` が含まれています。`;
      }
    }

    const customerNames = returnFields.customer ?? [];
    const contractNames = returnFields.contract ?? [];

    if (customerNames.length === 0 && contractNames.length === 0) {
      const hasCustomerKey = 'customer' in returnFields;
      const hasContractKey = 'contract' in returnFields;
      if (hasCustomerKey || hasContractKey) {
        return 'returnFields に空の配列のみが指定されています。';
      }
      return null;
    }

    const customerColumnNames = new Set(schemaColumns.filter((c) => c.destination === 'customer').map((c) => c.name));
    const contractColumnNames = new Set(schemaColumns.filter((c) => c.destination === 'contract').map((c) => c.name));

    const customerDuplicate = hasDuplicate(customerNames);
    if (customerDuplicate) {
      return customerDuplicate;
    }
    const contractDuplicate = hasDuplicate(contractNames);
    if (contractDuplicate) {
      return contractDuplicate;
    }

    for (const name of customerNames) {
      if (!customerColumnNames.has(name)) {
        return `returnFields.customer の列 \`${name}\` はスキーマに存在しないか、destination が customer ではありません。`;
      }
    }

    for (const name of contractNames) {
      if (!contractColumnNames.has(name)) {
        return `returnFields.contract の列 \`${name}\` はスキーマに存在しないか、destination が contract ではありません。`;
      }
    }

    return null;
  };
}
